import React, { useState, useEffect } from 'react';

import { pallette } from '../../config/pallette';
import ThumbsUpIcon from '../Icons/ThumbsUpIcon';
import ThumbsDownIcon from '../Icons/ThumbsDownIcon';
import SendAltIcon from '../Icons/SendAltIcon';
import api from '../../config/api';
import { useAuth } from '../../context/AuthContext';

import {
  FeedbackContainer,
  FeedbackButton,
  Tooltip,
  PositiveFeedbackMessage,
  FeedbackButtonContainer,
  Spinner,
  FeedbackTextContainer,
  FeedbackTextInstruction,
  FeedbackTextInput,
  FeedbackSendContainer,
  FeedbackTextLengthIndicator,
  FeedbackSendButton
} from './styles';

const Checkbox = ({ botResponse, userPrompt, handleScrollDown, conversationId }) => {
  const { userId } = useAuth();
  const [showTooltip, setShowTooltip] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [feedbackId, setFeedbackId] = useState(null);
  const [feedbackText, setFeedbackText] = useState('');
  const [feedbackSent, setFeedbackSent] = useState(false);
  let hideTimeout;

  useEffect(() => {
    if (selected && !feedbackText) {
      handleScrollDown();
    }
  }, [selected, feedbackText, handleScrollDown]);

  const handleMouseOver = (button) => {
    clearTimeout(hideTimeout);
    setShowTooltip(button);
  };

  const handleMouseOut = (button) => {
    hideTimeout = setTimeout(() => {
      setShowTooltip(button);
    }, 300);
  };

  const selectPositiveFeedback = async () => {
    setLoading(true);
    setFeedbackText('');
    setFeedbackSent(false);
    setShowTooltip(null);
    // Edit existing
    if (selected && feedbackId) {
      const response = await api.put(`${process.env.REACT_APP_API_URL}/api/feedback/${feedbackId}`, {
        feedback: true
       });
       if (response.status === 200) {
        setSelected('good');
       }
       setLoading(false);
    } else {
        // Create a new feedback
       const response = await api.post(`${process.env.REACT_APP_API_URL}/api/feedback`, {
        user_id: userId,
        user_prompt: userPrompt.text,
        bot_response: botResponse.text,
        feedback: true,
        conversation_id: conversationId
       });
       setLoading(false);
       if (response.status === 201) {
        setSelected('good');
        setFeedbackId(response.data.feedback_id);
       }
    }
  }

  const selectNegativeFeedback = async () => {
    setLoading(true);
    setFeedbackText('');
    setFeedbackSent(false);
    setShowTooltip(null);
    if (selected && feedbackId) {
      // Edit existing
      setLoading(true);
      const response = await api.put(`${process.env.REACT_APP_API_URL}/api/feedback/${feedbackId}`, {
        feedback: false
      });
      if (response.status === 200) {
        setSelected('bad');
       }
       setLoading(false);
    } else {
        // Create a new feedback
       const response = await api.post(`${process.env.REACT_APP_API_URL}/api/feedback`, {
        user_id: userId,
        user_prompt: userPrompt.text,
        bot_response: botResponse.text,
        feedback: false,
        conversation_id: conversationId
       });
       setLoading(false);
       if (response.status === 201) {
        setSelected('bad');
        setFeedbackId(response.data.feedback_id);
       }
    }
  }

  const handleInputChange = (event) => {
    setFeedbackText(event.target.value);
  };

  const sendTextFeedback = async () => {
    if (feedbackId) {
      setLoading(true);
      const response = await api.put(`${process.env.REACT_APP_API_URL}/api/feedback/message/${feedbackId}`, {
        feedback_message: feedbackText
      });
      setLoading(false);
      if (response.status === 200) {
        setFeedbackSent(true);
       }
    }
  }

  return (
    <FeedbackContainer>
      {loading ? (
        <Spinner />
      ) : (
        <FeedbackButtonContainer>
          <FeedbackButton
            onMouseEnter={() => handleMouseOver('good')}
            onMouseLeave={() => handleMouseOut(null)}
            onClick={() => selectPositiveFeedback()}
          >
            <ThumbsUpIcon fill={selected === 'good' ? pallette.main : pallette.text} />
          </FeedbackButton>
          <FeedbackButton
            onMouseEnter={() => handleMouseOver('bad')}
            onMouseLeave={() => handleMouseOut(null)}
            onClick={() => selectNegativeFeedback()}
          >
            <ThumbsDownIcon fill={selected === 'bad' ? pallette.main : pallette.text} />
          </FeedbackButton>
          {showTooltip === 'good' && (
            <Tooltip
              onClick={() => selectPositiveFeedback()}
              onMouseEnter={() => handleMouseOver('good')}
              onMouseLeave={() => handleMouseOut(null)}
              show={showTooltip}
            >
              Good Response
            </Tooltip>
          )}
          {showTooltip === 'bad' && (
            <Tooltip
              onClick={() => selectNegativeFeedback()}
              onMouseEnter={() => handleMouseOver('bad')}
              onMouseLeave={() => handleMouseOut(null)}
              show={showTooltip}
            >
              Bad Response
            </Tooltip>
          )}
        </FeedbackButtonContainer>
      )}
      {selected === 'good' && !loading && !feedbackSent && (
        <FeedbackTextContainer>
          <FeedbackTextInstruction>
            Tell us why this was a good response for you:
          </FeedbackTextInstruction>
          <FeedbackTextInput
            value={feedbackText}
            onChange={handleInputChange}
            maxLength={700}
          />
          <FeedbackSendContainer>
            <FeedbackTextLengthIndicator>{feedbackText.length}/700</FeedbackTextLengthIndicator>
            <FeedbackSendButton
              disabled={feedbackText.length <= 0 || feedbackText.length > 700}
              onClick={() => sendTextFeedback()}
            >
              <SendAltIcon />
            </FeedbackSendButton>
          </FeedbackSendContainer>
        </FeedbackTextContainer>
      )}
      {selected === 'good' && feedbackSent && (
        <PositiveFeedbackMessage>Thank you for the feedback</PositiveFeedbackMessage>
      )}
      {selected === 'bad' && !loading && !feedbackSent && (
        <FeedbackTextContainer>
          <FeedbackTextInstruction>
            Tell us why this was a bad response for you:
          </FeedbackTextInstruction>
          <FeedbackTextInput
            value={feedbackText}
            onChange={handleInputChange}
            maxLength={700}
          />
          <FeedbackSendContainer>
            <FeedbackTextLengthIndicator>{feedbackText.length}/700</FeedbackTextLengthIndicator>
            <FeedbackSendButton
              disabled={feedbackText.length <= 0 || feedbackText.length > 700}
              onClick={() => sendTextFeedback()}
            >
              <SendAltIcon />
            </FeedbackSendButton>
          </FeedbackSendContainer>
        </FeedbackTextContainer>
      )}
      {selected === 'bad' && feedbackSent && (
        <PositiveFeedbackMessage>Feedback submitted. Thank you</PositiveFeedbackMessage>
      )}
    </FeedbackContainer>
  );
};

export default Checkbox;